import React, { useCallback } from 'react';
import { Box, Chip, ListItemButton, Typography } from '@mui/material';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import type { SiteListItem as SiteListItemType } from '@billda/api';
import { SiteProgressAvatar } from '../atoms/SiteProgressAvatar';
import { ProgressBar } from '../atoms/ProgressBar';
import { formatCurrencyWhole } from '../../../../../shared/utils/currency';

interface SiteListItemProps {
  site: SiteListItemType;
  onClick: (siteId: number) => void;
}

export const SiteListItem = React.memo(function SiteListItem({
  site,
  onClick,
}: SiteListItemProps) {
  const handleClick = useCallback(() => onClick(site.id), [onClick, site.id]);
  const financials = site.financialMetrics;

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid="site-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {financials && (
        <SiteProgressAvatar
          slug={site.slug}
          name={site.name}
          progressPercentage={financials.progress}
          size={36}
        />
      )}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {site.name}
        </Typography>
        {site.what3WordsAddress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {site.what3WordsAddress}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Financial columns — hidden below sm */}
      {financials && (
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{ width: 95, display: 'flex', justifyContent: 'flex-start' }}
          >
            <Chip
              icon={<HomeWorkOutlinedIcon sx={{ fontSize: 13 }} />}
              label={`${site.plotCount} plots`}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 22,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                border: 'none',
                '& .MuiChip-icon': { color: 'text.secondary' },
              }}
            />
          </Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ width: 90, textAlign: 'left' }}
          >
            {formatCurrencyWhole(financials.totalLabourPrice)}
          </Typography>
          <Box sx={{ width: 110 }}>
            <ProgressBar percentage={financials.progress} />
          </Box>
        </Box>
      )}
    </ListItemButton>
  );
});
